import { ChevronDownIcon } from 'lucide-react';
import { useCallback, useSyncExternalStore, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * A collapsible group in the sidebar ("Jobs", "Organization"): a header row that toggles the items
 * under it, remembered per device. In the icon rail (sidebar collapsed) groups show no header, only
 * a hairline between them, and always stay expanded.
 */

const KEY = 'crewline-sidebar-sections';
const listeners = new Set<() => void>();
let closed: Set<string> | null = null;

function read(): Set<string> {
  if (closed) return closed;
  try {
    const raw = localStorage.getItem(KEY);
    closed = new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    closed = new Set();
  }
  return closed;
}
function write(next: Set<string>): void {
  closed = next;
  try {
    localStorage.setItem(KEY, JSON.stringify([...next]));
  } catch {
    /* ignore */
  }
  for (const l of listeners) l();
}
function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
const EMPTY = new Set<string>();

export function useSidebarSection(id: string): [boolean, () => void] {
  const set = useSyncExternalStore(subscribe, read, () => EMPTY);
  const open = !set.has(id);
  const toggle = useCallback(() => {
    const next = new Set(read());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    write(next);
  }, [id]);
  return [open, toggle];
}

export function SidebarSection({ id, label, collapsed, action, divider, className, children }: { id: string; label: string; collapsed: boolean; action?: ReactNode; /** Hairline above the group in the icon rail. */ divider?: boolean; className?: string; children: ReactNode }) {
  const [open, toggle] = useSidebarSection(id);
  if (collapsed) {
    return (
      <>
        {divider && <div className="bg-sidebar-border mx-3 my-3 h-px" />}
        {children}
      </>
    );
  }
  return (
    <div className={className}>
      <div className="flex items-center justify-between px-3 pb-1">
        <button type="button" onClick={toggle} aria-expanded={open} aria-controls={`sidebar-section-${id}`} className="text-muted-foreground hover:text-foreground flex h-7 items-center gap-1 px-1 text-xs font-medium tracking-wide uppercase transition-colors">
          <ChevronDownIcon className={cn('size-3.5 transition-transform duration-200', !open && '-rotate-90')} aria-hidden />
          {label}
        </button>
        {action}
      </div>
      <div id={`sidebar-section-${id}`} className={cn('grid transition-[grid-template-rows] duration-200 ease-out', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')} inert={!open}>
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
}
